import { GAME_CONFIG, type AchievementConfig, type GameConfig, type UpgradeLevelConfig } from "../config/gameConfig";
import { dailyGoalsComplete, dailyGoalsCompletedCount } from "./liveOps";
import type { AchievementId, GameState, UpgradeCode, UpgradeLevel } from "./types";

export type AchievementProgress = {
  current: bigint;
  target: bigint;
  ready: boolean;
  comparison: "at-least" | "at-most";
  lockedUntilSeasonFinal: boolean;
};

export type ReadyRewardSummary = {
  achievements: number;
  daily: number;
  season: number;
  total: number;
};

const ENDED_SEASON_STATUSES = new Set<GameState["season"]["status"]>([
  "claimable",
  "closed",
]);

function completedSeasonChains(state: GameState): number {
  const completed = new Set<string>();
  for (const task of GAME_CONFIG.liveOps.seasonTasks) {
    if (task.claimPhase === "season-end" || task.chainId == null || task.tier == null) continue;
    const highestTier = GAME_CONFIG.liveOps.seasonTasks
      .filter((candidate) => candidate.enabled && candidate.claimPhase !== "season-end" && candidate.chainId === task.chainId)
      .reduce((highest, candidate) => Math.max(highest, candidate.tier ?? 0), 0);
    if (task.tier === highestTier && state.progress.seasonal.claimedCodes.includes(task.code)) completed.add(task.chainId);
  }
  return completed.size;
}

export function coreUpgradeLevel(code: UpgradeCode, config: GameConfig = GAME_CONFIG): number {
  const track = config.upgrades[code];
  return track.coreMaxLevel ?? track.levels.at(-1)?.level ?? track.initialLevel;
}

function maxedUpgradeBranches(state: GameState): number {
  return (Object.keys(GAME_CONFIG.upgrades) as UpgradeCode[]).filter((code) => {
    return getUpgradeLevel(state, code) >= coreUpgradeLevel(code);
  }).length;
}

export function getUpgradeLevel(state: Pick<GameState, "upgrades">, code: UpgradeCode): UpgradeLevel {
  if (code === "precisionCutter") return state.upgrades.precisionCutterLevel;
  if (code === "mechanicalCutter") return state.upgrades.mechanicalCutterLevel;
  if (code === "energyCapacity") return state.upgrades.energyCapacityLevel;
  return state.upgrades.energyRecoveryLevel;
}

export function getUpgradeLevelConfig(
  code: UpgradeCode,
  level: UpgradeLevel,
  config: GameConfig = GAME_CONFIG,
): UpgradeLevelConfig {
  const definition = config.upgrades[code].levels.find((candidate) => candidate.level === level);
  if (definition === undefined) throw new Error(`Missing ${code} level ${level}`);
  return definition;
}

export function getNextUpgradeLevel(
  state: GameState,
  code: UpgradeCode,
  config: GameConfig = GAME_CONFIG,
): UpgradeLevelConfig | null {
  const current = getUpgradeLevel(state, code);
  return config.upgrades[code].levels.find((candidate) => candidate.level === current + 1) ?? null;
}

export function deriveWorkshopStage(state: Pick<GameState, "upgrades">, config: GameConfig = GAME_CONFIG): 1 | 2 | 3 | 4 {
  const purchased = getPurchasedUpgradeCount(state, config);
  const thresholds = config.workshop.visualStageUpgradeThresholds;
  if (purchased >= thresholds[3]) return 4;
  if (purchased >= thresholds[2]) return 3;
  if (purchased >= thresholds[1]) return 2;
  return 1;
}

export function getPurchasedUpgradeCount(
  state: Pick<GameState, "upgrades">,
  config: GameConfig = GAME_CONFIG,
): number {
  return (Object.keys(config.upgrades) as UpgradeCode[]).reduce((total, code) => {
    const initial = config.upgrades[code].initialLevel;
    return total + Math.max(0, getUpgradeLevel(state, code) - initial);
  }, 0);
}

export function achievementProgress(
  state: GameState,
  achievement: AchievementConfig,
): AchievementProgress {
  let current: bigint;
  if (achievement.metric === "taps") current = BigInt(state.progress.confirmedTaps);
  else if (achievement.metric === "mechanical-level") current = BigInt(state.upgrades.mechanicalCutterLevel);
  else if (achievement.metric === "precision-level") current = BigInt(state.upgrades.precisionCutterLevel);
  else if (achievement.metric === "energy-capacity-level") current = BigInt(state.upgrades.energyCapacityLevel);
  else if (achievement.metric === "workshop-stage") current = BigInt(state.workshopStage);
  else if (achievement.metric === "passive-produced") current = BigInt(state.progress.passiveProduced);
  else if (achievement.metric === "arena-matches") current = BigInt(state.progress.seasonal.arenaMatches);
  else if (achievement.metric === "arena-wins") current = BigInt(state.progress.seasonal.arenaWins);
  else if (achievement.metric === "friends") current = BigInt(state.friends.length);
  else if (achievement.metric === "daily-streak") current = BigInt(state.progress.dailyStreak.longest);
  else if (achievement.metric === "season-chains-completed") current = BigInt(completedSeasonChains(state));
  else if (achievement.metric === "maxed-upgrade-branches") current = BigInt(maxedUpgradeBranches(state));
  else if (achievement.metric === "pvp-wins") current = BigInt(state.progress.seasonal.pvpWins);
  else if (achievement.metric === "active-friends") current = BigInt(state.progress.seasonal.activeInvites);
  else if (achievement.metric === "season-percentile") {
    const participantCount = Math.max(1, state.season.projection.participantCount);
    const rank = state.season.projection.rank ?? participantCount;
    current = BigInt(Math.max(1, Math.ceil((rank * 100) / participantCount)));
  } else current = BigInt(state.season.projection.rank ?? Math.max(1, state.season.projection.participantCount));
  const target = BigInt(achievement.target);
  const comparison = achievement.comparison ?? "at-least";
  const needsFinalRanking = achievement.metric === "season-percentile" || achievement.metric === "season-rank";
  const lockedUntilSeasonFinal = achievement.finalSeasonOnly === true && (
    !ENDED_SEASON_STATUSES.has(state.season.status) ||
    (needsFinalRanking && (state.season.projection.provisional || state.season.projection.rank === null))
  );
  const reached = comparison === "at-most" ? current <= target : current >= target;
  // Final signs are granted by settlement, never by the manual claim endpoint.
  const ready = state.season.status === "active" && achievement.finalSeasonOnly !== true && reached;
  return { current, target, comparison, lockedUntilSeasonFinal, ready };
}

export function achievementById(
  id: AchievementId,
  config: GameConfig = GAME_CONFIG,
): AchievementConfig {
  const achievement = config.progress.achievements.find((candidate) => candidate.id === id);
  if (achievement === undefined) throw new Error(`Missing achievement ${id}`);
  return achievement;
}

export function readyRewardSummary(
  state: GameState,
  config: GameConfig = GAME_CONFIG,
): ReadyRewardSummary {
  const achievements = config.progress.achievements.filter(
    (achievement) =>
      !state.progress.claimedAchievementIds.includes(achievement.id) && achievementProgress(state, achievement).ready,
  ).length;
  const daily = state.season.status === "active" && !state.progress.daily.rewardClaimed && dailyGoalsComplete(state, config) ? 1 : 0;
  const listedSeasonClaims = state.seasonClaims.filter((claim) => claim.status === "claimable").length;
  const season = Math.max(listedSeasonClaims, state.season.claimStatus === "claimable" ? 1 : 0);
  return { achievements, daily, season, total: achievements + daily + season };
}

export { dailyGoalsComplete, dailyGoalsCompletedCount };
